import React, { useState, useMemo, useRef, createContext, useEffect } from 'react';
import { motion } from 'framer-motion';
import ChatTabMenu from '../components/ChatTabMenu';
import { getChatActions } from '../helpers/chatActions';
import Messages from '../components/Messages';
import MultiMessages from '../components/MultiMessages';
import QAModeratedMessages from '../components/QAModeratedMessages';
import ChatSubmit from '../components/ChatSubmit';
import { useDimensions } from '../hooks/useDimensions';
import { MenuToggle } from '../components/MenuToggle';
import VisitorAwareness from '../components/VisitorAwareness';
import { JUNOContent } from '../../types/global';
import LiveSessionTime from '../components/LiveSessionTime';
import { addEventListener, removeEventListenerByScope } from '../helpers/pubNubFunctions';
import { FontAwesomeConfigurations } from '../../utils/Configuration/FontAwesomeConfiguration';
import { JUNOSelectOption } from '../components/MultiMessages';

interface ChatPaneState {
  content: JUNOContent;
  isModerator: boolean;
  isPlatformModerator: boolean;
  viewUser: Function;
  selectedTab: number;
  activeMessenger: string;
  isOpen: boolean;
  actions: any;
  getImgSrc: Function;
  pollActive: boolean;
  multiMessageOptions: JUNOSelectOption[];
  lastTab?: number;
}
interface ChatPaneContext {
  context: ChatPaneState;
  setContext: Function;
}

interface IChatPaneProps {
  onOpen: () => void;
  onClose: () => void;
}

const ChatContext = createContext<ChatPaneContext | undefined>(undefined);

function ChatProvider({
  children,
  content,
  isModerator,
  isPlatformModerator,
  viewUser,
  getImgSrc,
  startsOpen = false,
}) {
  const [context, setContext] = useState<ChatPaneState>({
    selectedTab: 0,
    activeMessenger: 'chat',
    content: content,
    isModerator: isModerator,
    isPlatformModerator: isPlatformModerator,
    viewUser: viewUser,
    isOpen: startsOpen,
    actions: getChatActions(content),
    getImgSrc: getImgSrc,
    pollActive: false,
    lastTab: undefined,
    multiMessageOptions: getMultiMessageOptionsArray(getChatActions(content)),
  });

  // Give app access to the state setter
  window.ReactChatPane = {
    setContext: setContext,
  };

  const value = { context, setContext };
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const context = React.useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useCount must be used within a CountProvider');
  }
  return context;
}

export default function ChatPaneWrapper(props: ChatPaneState & IChatPaneProps) {
  let { onOpen, onClose } = props;
  return (
    <ChatProvider {...props}>
      <ChatPane onClose={onClose} onOpen={onOpen} />
    </ChatProvider>
  );
}

// Create array of options for the MultiMessages dropdown
function getMultiMessageOptionsArray(actions): JUNOSelectOption[] {
  const optionArray: JUNOSelectOption[] = [];
  const chat = actions.find(action => action.id === 'chat');
  if (chat) {
    optionArray.push({
      name: chat.name,
      value: chat.id,
      notification: false,
    });

    chat.data.cohorts.forEach(cohort => {
      optionArray.push({
        name: cohort.Name,
        value: `group_${cohort.Id}`,
        notification: false,
      });
    });
  }
  return optionArray;
}

function ChatPane(props: IChatPaneProps) {
  const { onOpen, onClose } = props;
  const {
    context: { content, selectedTab, isModerator, activeMessenger, isOpen, actions },
    setContext,
  } = useChat();

  const [keyboardPadding, setKeyboardPadding] = useState(0);

  if (actions.length === 0) {
    return <></>;
  }

  window.ReactChatPane = {
    setContext: setContext,
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const { height } = useDimensions(containerRef);

  const toggleSidebar = () => {
    setContext(old => {
      return {
        ...old,
        isOpen: !old.isOpen,
      };
    });
  };

  const closeSidebar = () => {
    setContext(old => {
      return {
        ...old,
        isOpen: false,
      };
    });
  };

  // current room
  const room = useMemo(() => {
    if (actions[selectedTab].data.channeltype == OTSessionContent.CHANNELTYPE_CHAT && activeMessenger !== 'chat') {
      return actions[selectedTab].data?.cohortChannels.find(channel => channel.type == activeMessenger).room;
    } else {
      return content.roomForChannelType(actions[selectedTab].data.channeltype);
    }
  }, [selectedTab, activeMessenger]);

  // Callbacks from outside chat pane
  React.useEffect(() => {
    if (isOpen) {
      onOpen();
    } else {
      onClose();
    }
  }, [isOpen]);

  const onChatSubmitFocus = e => {
    /* no longer needed with app resizing
    if (Environment.TARGET == E_Target.ANDROID) {
      //can't get keyboard height
      const padding = 330 - (window.innerHeight - containerRef.current.getBoundingClientRect().bottom);
      setKeyboardPadding(padding);
    }
    */
  };

  const onChatSubmitBlur = e => {
    /* no longer needed with app resizing
    if (Environment.TARGET == E_Target.ANDROID) {
      //can't get keyboard height
      setKeyboardPadding(0);
    }
    */
  };

  // Open to polls tab if a poll gets posted
  useEffect(() => {
    /*
    const _keyboardHeightWillChangeHandler = event => {
      //need to pad for keyboard, but subtract whatever space is below the panel
      var padding = 0;
      if (event.keyboardHeight > 0) {
        padding = event.keyboardHeight - (window.innerHeight - containerRef.current.getBoundingClientRect().bottom);
      }
      setKeyboardPadding(padding);
    };

    if (App.CORDOVA) {
      window.addEventListener('keyboardHeightWillChange', _keyboardHeightWillChangeHandler);
    }
    */

    addEventListener(
      PubNubCenter.ON_MESSAGE,
      (event, data) => {
        // grab room and tab number
        const pollsActionIndex = actions.findIndex(
          action => action.data.channeltype == OTSessionContent.CHANNELTYPE_POLL,
        );

        if (data?.channel == content.roomForChannelType(OTSessionContent.CHANNELTYPE_POLL)) {
          if (data?.message?.data?.modType === 'pollposted') {
            setContext(old => {
              return {
                ...old,
                isOpen: true,
                selectedTab: pollsActionIndex,
              };
            });
          }
        }
      },
      'chat_pane_listeners',
    );

    return () => {
      removeEventListenerByScope(
        //@ts-ignore
        PubNubCenter.ON_MESSAGE,
        'chat_pane_listeners',
      );

      if (App.CORDOVA) {
        // window.removeEventListener('keyboardHeightWillChange', _keyboardHeightWillChangeHandler);
      }
    };
  }, []);

  const sidebar = {
    open: (height = 1000) =>
      App.INSTANCE.isPhone()
        ? {
            clipPath: `circle(${height * 2 + 200}px at calc(100% - 40px) calc(100% - 40px))`,
            transition: {
              type: 'spring',
              stiffness: 20,
              restDelta: 2,
            },
          }
        : {
            clipPath: `circle(${height * 2 + 200}px at calc(100% - 40px) 70px)`,
            transition: {
              type: 'spring',
              stiffness: 20,
              restDelta: 2,
            },
          },
    closed: App.INSTANCE.isPhone()
      ? {
          clipPath: 'circle(25px at calc(100% - 40px) calc(100% - 40px))',
          transition: {
            delay: 0.0,
            type: 'spring',
            stiffness: 400,
            damping: 40,
          },
        }
      : {
          clipPath: 'circle(25px at calc(100% - 40px) 70px)',
          transition: {
            delay: 0.0,
            type: 'spring',
            stiffness: 400,
            damping: 40,
          },
        },
  };

  return (
    <>
      <motion.nav initial={false} animate={isOpen ? 'open' : 'closed'} custom={height} ref={containerRef}>
        <motion.div className="background" variants={sidebar} />
        <MenuToggle toggle={toggleSidebar} isOpen={isOpen} />
        <motion.div
          className={'stream_chat_pane'}
          initial={`hidden`}
          variants={variants}
          animate={isOpen ? 'visible' : 'hidden'}
        >
          {/*  */}
          <motion.div
            className={'stream_chat_pane_inner'}
            initial={`hidden`}
            animate={isOpen ? 'visible' : 'hidden'}
            variants={innerVariants}
            style={{ paddingBottom: `${keyboardPadding}px` }}
          >
            <div onClick={closeSidebar} className={`close_chat_pane ${isModerator && 'mod'}`}>
              <i className={FontAwesomeConfigurations.ICON_XMARK}></i>
            </div>
            {isModerator &&
              (content.FirstBucketName() == CrowdHub_App.BUCKET_OTSESSION ||
                content.FirstBucketName() == CrowdHub_App.BUCKET_GROUPOTSESSION ||
                content.FirstBucketName() == CrowdHub_App.BUCKET_BREAKOUTOTSESSION) && (
                <LiveSessionTime content={content} showCountDownOnLive={true} />
              )}
            <ChatTabMenu tabs={actions} />
            <div className={`messages_container`}>
              {actions.map((action, i) => {
                if (
                  action.data.channeltype ==
                    // @ts-ignore
                    OTSessionContent.CHANNELTYPE_QA &&
                  isModerator &&
                  action.data.moderateFlow
                ) {
                  return <QAModeratedMessages key={`messages_${i}`} {...action.data} showing={i == selectedTab} />;
                } else if (
                  action.data.channeltype ==
                    // @ts-ignore
                    OTSessionContent.CHANNELTYPE_CHAT &&
                  action.data.cohortChannels.length > 0
                ) {
                  return (
                    <MultiMessages
                      key={`messages_${i}`}
                      {...action.data}
                      showing={i == selectedTab}
                      activeMessenger={activeMessenger}
                    />
                  );
                } else if (
                  action.data.channeltype ==
                  // @ts-ignore
                  OTSessionContent.CHANNELTYPE_UPDATE
                ) {
                  return (
                    <VisitorAwareness
                      room={content.roomForChannelType(OTSessionContent.CHANNELTYPE_UPDATE)}
                      showing={i == selectedTab}
                      key={'visitorawareness'}
                    />
                  );
                }
                return <Messages key={`messages_${i}`} {...action.data} showing={i == selectedTab} />;
              })}
            </div>
            <div
              className={`submit_container ${actions[selectedTab].data.channeltype} ${
                isModerator ? 'mod' : 'attendee'
              }`}
            >
              <ChatSubmit action={actions[selectedTab]} room={room} />
            </div>
          </motion.div>
        </motion.div>
      </motion.nav>
    </>
  );
}

// Animation variants
const variants = {
  hidden: {
    opacity: 0,
    visibility: 'hidden',
    transition: {
      duration: 0.2,
      damping: 35,
      delay: 0,
    },
  },
  visible: {
    opacity: 1,
    visibility: 'visible',
    transition: {
      duration: 0.2,
      damping: 35,
      delay: 0.4,
    },
  },
};

const innerVariants = {
  hidden: {
    opacity: 1,
    transition: {
      duration: 0,
    },
  },
  visible: {
    opacity: 1,
    transition: {
      delay: 0.2,
    },
  },
};
